/**
 * Shared fetch-based AJAX utility.
 * Replaces the copy-pasted ppAjax() jQuery wrappers across 15+ files.
 */

export interface PpFetchSettings {
  url: string;
  method?: string;
  data?: unknown;
  headers?: Record<string, string>;
}

/**
 * JSON fetch with automatic stringify and content-type header.
 * Drop-in replacement for the old ppAjax wrapper.
 */
export async function ppFetch<T = unknown>(settings: PpFetchSettings): Promise<T> {
  const headers: Record<string, string> = { ...settings.headers };
  const init: RequestInit = {
    method: settings.method ?? 'GET',
  };
  if (settings.data !== undefined) {
    headers['Content-Type'] = 'application/json; charset=utf-8';
    init.body = JSON.stringify(settings.data);
  }
  if (Object.keys(headers).length > 0) {
    init.headers = headers;
  }
  const response = await fetch(settings.url, init);
  if (!response.ok) {
    throw new Error(`ppFetch: ${response.status} ${response.statusText} for ${settings.url}`);
  }
  const text = await response.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

/**
 * Raw fetch for non-JSON endpoints (e.g. HTML fragments, WordPress API).
 */
export async function ppFetchRaw(url: string, init?: RequestInit): Promise<Response> {
  return fetch(url, init);
}

/**
 * Fetch that returns the raw text (for HTML fragment loading in modals).
 */
export async function ppFetchHtml(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`ppFetchHtml: ${response.status} for ${url}`);
  }
  return response.text();
}
